import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/cambodia-countryside-experience.jpg";

const itinerary = [
  {
    time: "Stop 1 · Local Market",
    title: "Lively Buying & Selling",
    body:
      "Begin in Chreav with a stroll through the bustling local market. Watch farmers and villagers trade fresh produce, fish and household goods a true window into everyday Cambodian rural life.",
  },
  {
    time: "Stop 2 · Village Walk",
    title: "Daily Farming Routines",
    body:
      "Walk through the village to see rice fields, seasonal vegetable plots and traditional stilt houses. Depending on the season, join farmers in rice planting, harvesting or vegetable cultivation alongside our community guide.",
  },
  {
    time: "Stop 3 · Ox-Cart Ride",
    title: "Through the Countryside",
    body:
      "Climb aboard a traditional wooden ox-cart, pulled by oxen or buffalo as Khmer farmers have done for generations. Roll slowly past rice paddies and palm trees perfect for photos and a quiet, scenic moment in rural Cambodia.",
  },
  {
    time: "Stop 4 · Rice Milling & Snacks",
    title: "Demonstration & Coconut Juice",
    body:
      "Watch a traditional rice-milling demonstration using local tools and methods, then end your visit at a welcoming village home with refreshing coconut juice and Khmer snacks before returning to your hotel.",
  },
];

const includes = [
  "All entrance fees",
  "Professional English-speaking guide",
  "Air-conditioned transportation with hotel pick-up & drop-off",
  "Complimentary bottled water & cold towels",
];

const excludes = [
  "Tips and gratuities",
  "Personal expenses",
  "Travel insurance",
];

const essentials = [
  "Modest clothing",
  "Comfortable walking shoes",
  "Sun protection & insect repellent",
  "Light rain jacket",
];

const TourOxcartVillage = () => {
  return (
    <main className="bg-background">
      {/* HERO */}
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Chreav village ox-cart and rice farm private tour near Siem Reap with French-speaking guide, authentic Cambodia countryside experience"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Half-Day Village Experience</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance">
              Village Life <em className="font-light">with</em> Ox-Cart & Farm.
            </h1>
            <p className="mt-10 max-w-2xl text-background/75 font-light text-lg leading-relaxed">
              Chreav Village an authentic farming experience. Walk a working rice community, ride a traditional ox-cart through the paddies, watch a rice-milling demonstration, and share coconut juice with a local family.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 0.5 Day</span>
              <span>Group · 1–12</span>
              <span>Pickup · 8:00 AM / 2:30 PM</span>
            </div>
          </div>
        </div>
      </header>

      {/* INTRO */}
      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Idea</p>
          <h2 className="font-display text-4xl leading-[1.05] mb-10 text-balance md:text-xl">
            Rice fields, ox-carts, and a welcome at a village home.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              Experience the charm of rural life with an authentic ox-cart ride. Traditionally pulled by oxen or buffalo, these wooden carts have long been used by local farmers to transport crops, seedlings and daily essentials. Today, our community invites visitors to enjoy this unique journey through the countryside where you can truly feel the warmth, simplicity and traditions of village life.
            </p>
            <p>
              Discover the peaceful rhythms of Cambodian rural life in Chreav, a farming community where rice and seasonal vegetables shape everyday living. Witness lively buying and selling at the local market, walk through the village to see daily farming routines, and join farmers in seasonal activities like rice planting, harvesting or vegetable cultivation.
            </p>
            <p>
              Travel through the village on a traditional ox-cart perfect for scenic photos then watch a traditional rice-milling demonstration using local tools and methods. End your visit with refreshing coconut juice and local snacks at a welcoming village home. Perfect for travellers seeking culture, nature, community engagement, photography and authentic experiences.
            </p>
          </div>
        </div>
      </section>

      {/* ITINERARY */}
      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
                A half-day, four stops.
              </h2>
              <ol className="space-y-px">
                {itinerary.map((s, idx) => (
                  <li
                    key={idx}
                    className="group grid grid-cols-[auto_1fr] md:grid-cols-[200px_1fr] gap-x-4 md:gap-x-12 min-w-0 border-t border-border py-8 md:py-10 transition-colors hover:bg-secondary/40 last:border-b"
                  >
                    <div className="flex flex-col items-start gap-y-2 text-base">
                      <span className="font-display text-primary tabular-nums text-xl">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="uppercase tracking-[0.25em] text-primary text-xs md:text-base">
                        {s.time}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">
                        {s.title}
                      </h3>
                      <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">
                        {s.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <BookingSidebar pricePerPerson={95} tourTitle="Village Life Tour with Ox-Cart Ride and Farm Activities" />
          </div>
        </div>
      </section>

      {/* INCLUDES / EXCLUDES */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— What's Included</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Everything you need for the visit.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            <div>
              <h3 className="font-display text-2xl mb-5">Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {includes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Not Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {excludes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* GOOD TO KNOW */}
      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Plan your visit.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-4">Essential Items</h3>
              <ul className="space-y-2 font-light text-muted-foreground">
                {essentials.map((it) => (
                  <li key={it}>• {it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Weather</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Varied climate across regions. Hot in lowlands, cooler in Mondulkiri highlands. Coastal breezes near islands.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Food</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Diverse regional cuisine. Fresh seafood on the coast, Kampot pepper specialties, and traditional Khmer dishes throughout.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Additional Information</h3>
              <ul className="space-y-2 font-light text-muted-foreground">
                <li>• Pickup time confirmed one day before</li>
                <li>• Dress code: casual but respectful</li>
                <li>• Children under 5 years old are not permitted</li>
                <li>• Maximum group size: 1–12 travellers</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TourOxcartVillage;
